/**
 * Hostname → shell classifier for the three-shell deployment.
 *
 * The single Vite bundle serves three roles depending on the host:
 *
 *   ``forgmatic.com`` / ``www.forgmatic.com`` → ``platform``
 *     The SaaS marketing site: landing, pricing plans, sign-up,
 *     for-providers / for-business, public provider directory.
 *
 *   ``admin.forgmatic.com`` → ``platform-admin``
 *     Cross-tenant Forgmatic-staff console: customer directory,
 *     audit log across tenants, global feature flags.
 *
 *   anything else (``demo.forgmatic.com``, ``*.forgmatic.com``,
 *   any custom CNAME a tenant has attached) → ``tenant``
 *     The DXF cutting service shell rendered for the tenant resolved
 *     by ``TenantContextMiddleware`` from the Host header.
 *
 * The classifier is intentionally cheap and synchronous so the
 * top-level `<RouterProvider>` can pick the right tree on the very
 * first render — no Suspense, no double-paint.
 *
 * The legacy ``forgmatic.cz`` apex is treated as ``platform`` while
 * the 301 redirect from PR 9 is in flight; once DNS is sunset the
 * alias can be dropped.
 *
 * Dev / preview hostnames (``localhost``, IP literals) default to
 * ``tenant`` because the bulk of the local dev surface (cart,
 * checkout, quote, materials, admin) lives on the tenant shell, and
 * the e2e / Playwright suite drives URLs from that surface against
 * the vite preview. Override via the ``VITE_FORCE_SHELL`` env var
 * when you need to develop the marketing or platform-admin shell
 * against a dev server that doesn't have a proper subdomain
 * (``VITE_FORCE_SHELL=platform npm run dev``). Vercel preview deploys
 * stay on ``platform`` so reviewers land on the marketing tree.
 */

import {
  PLATFORM_APEX_DOMAINS,
  TENANT_APEX_DOMAINS,
  apexOriginOfKind,
  isPlatformAdminHost,
  normalizeHostname,
} from "@/shell/tenantHosts";

export type ShellKind = "platform" | "platform-admin" | "tenant";

/** The marketing hosts: every platform apex plus its ``www``. */
function isPlatformMarketingHost(host: string): boolean {
  return PLATFORM_APEX_DOMAINS.some(
    (apex) => host === apex || host === `www.${apex}`,
  );
}

/**
 * Classify a hostname into a shell kind. Pure function — exported
 * separately from the window-bound default below so vitest can drive
 * it with explicit hostnames without monkeypatching ``window``.
 */
export function classifyHostname(hostname: string): ShellKind {
  const host = hostname.trim().toLowerCase().replace(/\.$/, "");

  // 🚨 Exact ``admin.{platformApex}``, never a bare ``admin.`` prefix.
  // Once tenants have their own zone, ``admin.{tenantApex}`` sits in a
  // namespace strangers provision themselves into, and a prefix test
  // would render the cross-tenant staff console there.
  if (isPlatformAdminHost(host)) return "platform-admin";
  if (isPlatformMarketingHost(host)) return "platform";

  // Vercel preview deploys (`<project>-<hash>-<team>.vercel.app`)
  // default to the platform shell so reviewers land on something
  // sensible when clicking the deploy URL from a PR.
  if (host.endsWith(".vercel.app")) return "platform";

  // Everything else — tenant subdomains, custom CNAMEs, dev hosts
  // (``localhost``, ``127.0.0.1``) — falls through to the tenant
  // shell. Dev defaults to tenant because that's where buyer + admin
  // live; opt into platform via ``VITE_FORCE_SHELL=platform``.
  return "tenant";
}

/**
 * Resolve the active shell from ``window.location.hostname``, with
 * a ``VITE_FORCE_SHELL`` env override for local development.
 *
 * SSR-safe: returns ``tenant`` when ``window`` is undefined (matches
 * the dev / e2e default) so the router can be constructed during
 * pre-render without throwing.
 */
export function resolveShell(): ShellKind {
  const forced = (import.meta.env?.VITE_FORCE_SHELL as string | undefined)?.trim();
  if (forced === "platform" || forced === "platform-admin" || forced === "tenant") {
    return forced;
  }

  if (typeof window === "undefined" || !window.location) return "tenant";
  return classifyHostname(window.location.hostname);
}

// Marketing-shell components ("Go to your workspace", post-signup
// redirect) and every "look at the demo shop" CTA need a stable tenant
// origin to deep-link into. The demo tenant always exists per the ab40
// migration, so it's a safe default.
// Override per-environment via ``VITE_DEFAULT_TENANT_HOST``.
//
// 🚨 Spelled on the TENANT apex (``forgmatic.app``), not the platform
// one. In the browser only the leading label survives — the apex comes
// from the page — but during pre-render there is no ``window`` to read
// one off and this host is printed VERBATIM. On ``demo.forgmatic.com``
// that address only worked because a 307 caught it.
const FALLBACK_TENANT_HOST = "demo.forgmatic.app";

export function defaultTenantHost(): string {
  const env = (import.meta.env?.VITE_DEFAULT_TENANT_HOST as string | undefined)?.trim();
  return env && env.length > 0 ? env : FALLBACK_TENANT_HOST;
}

/**
 * Build an absolute URL into a tenant subdomain, **within the deployment
 * the page is currently served from**.
 *
 * - ``slug`` provided → ``{scheme}//{slug}.{apex}{:port}{path}``
 * - ``slug`` omitted  → the default tenant on that same apex
 *
 * The path is taken verbatim — caller owns the leading slash.
 *
 * Used by the OrgSwitcher, the platform-shell AccountMenu and the
 * post-signup redirect to hop the user between tenant hosts, where
 * ``TenantContextMiddleware`` resolves the org from the Host header.
 *
 * 🚨 The session does NOT carry. ``COOKIE_DOMAIN`` used to cover every
 * hop and no longer does: session cookies are host-only outside the
 * platform zone, so a hop that leaves that zone lands on a sign-in
 * form unless it carries a one-time transfer token and redeems it
 * through ``/auth/continue`` on the landing host.
 *
 * **Use this builder only for a destination that needs no session.**
 * For anything authenticated use ``tenantHopHref`` (a plain ``<a>``,
 * via the ``/auth/hop`` interstitial) or ``tenantHopUrl`` (when you
 * already hold a token from the API). ``repo_checks buildtenanturl-
 * callers`` fails the build on a new caller here, because a bare hop
 * to an authenticated page does not break until ``TENANT_DOMAIN`` is
 * set — it is a latent bug that ships green.
 *
 * 🚨 Scheme, apex and port are read from ``window.location``, not baked
 * in. They used to be the literals ``https://`` and ``forgmatic.com``,
 * which meant every hop off a dev server left the machine: picking a
 * tenant in the switcher on ``stark.localhost:5174`` navigated to
 * ``https://tycho.forgmatic.com`` — production, wrong data, no session.
 *
 * Falls back to the production apex only when the current host belongs
 * to no known apex (SSR, Vercel preview, unrecognised custom domain).
 */
export function buildTenantUrl(path: string, slug?: string | null): string {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const current = apexOriginOfKind("tenant");
  if (current) {
    const label = slug && slug.length > 0 ? slug : defaultTenantLabel();
    const port = current.port ? `:${current.port}` : "";
    return `${current.protocol}//${label}.${current.apex}${port}${suffix}`;
  }
  const host =
    slug && slug.length > 0
      ? `${slug}.${FALLBACK_PLATFORM_HOST}`
      : defaultTenantHost();
  return `https://${host}${suffix}`;
}

/**
 * The subdomain label of ``defaultTenantHost()`` — ``demo`` out of
 * ``demo.forgmatic.app`` — so the no-slug case lands on the same tenant
 * on whichever apex is live.
 *
 * 🚨 A default host whose apex this deployment does not carry still
 * yields its LEADING LABEL. The default names a tenant SLUG; the apex
 * spelled next to it is just how it is configured, and the live one
 * comes from ``apexOriginOfKind``. Returning ``null`` here instead —
 * which is what this did until KAN-1021 — made ``buildTenantUrl`` drop
 * the label and build the BARE apex: production sets
 * ``VITE_TENANT_APEX_DOMAINS=forgmatic.app,…`` while this default was
 * spelled on ``.com``, so every demo-shop CTA on the marketing site
 * pointed at ``https://forgmatic.app/cs`` — a hostname that does not
 * resolve at all (the zone carries ``demo``/``www``/… but no apex
 * record). Measured on the live site 2026-09-07.
 *
 * ``jsdom`` and the dev server never saw it: their apex list is the
 * built-in default, which still carries ``forgmatic.com``, so the
 * ``.com``-spelled default matched and the label came out fine.
 */
export function tenantLabelOf(
  rawHost: string,
  apexes: readonly string[],
): string {
  const host = normalizeHostname(rawHost);
  const apex = apexes.find((d) => host.endsWith(`.${d}`));
  if (apex) return host.slice(0, -(apex.length + 1));
  const dot = host.indexOf(".");
  return dot > 0 ? host.slice(0, dot) : host;
}

function defaultTenantLabel(): string {
  return tenantLabelOf(defaultTenantHost(), TENANT_APEX_DOMAINS);
}

/**
 * Absolute URL into a tenant host that CARRIES THE SESSION.
 *
 * ``transferToken`` present → route through ``/auth/continue`` on the
 * destination, which exchanges it for a cookie there. Absent → the
 * plain path, because the API said this hop needs no handover (the two
 * hosts share a cookie, or the deploy has no tenant apex).
 *
 * Only the API decides which of those it is, and it decides in
 * ``cookies.hop_needs_session_transfer``. The FE has every input it
 * would need to work it out itself and must not: two copies of that
 * rule is how one of them ends up asking ``scope(a) != scope(b)``,
 * which reads right and answers "no token needed" for exactly the
 * tenant→tenant hop that needs one.
 */
export function tenantHopUrl(
  path: string,
  slug: string | null | undefined,
  transferToken: string | null | undefined,
): string {
  if (!transferToken) return buildTenantUrl(path, slug);
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return buildTenantUrl(
    `/auth/continue?t=${encodeURIComponent(transferToken)}` +
      `&next=${encodeURIComponent(suffix)}`,
    slug,
  );
}

/**
 * SAME-ORIGIN href for an ``<a>`` that leads to an authenticated page
 * on ``slug``'s tenant host.
 *
 * The token has to be minted on the host that holds the session, which
 * is this one — so the link cannot point at the destination directly.
 * It points at ``/auth/hop`` here instead, which mints and then
 * forwards.
 *
 * An interstitial rather than an ``onClick`` handler because these are
 * menu entries: turning them into buttons to run a mutation first
 * would cost middle-click, ⌘-click and "open in new tab", which is how
 * people actually open a second workspace.
 */
export function tenantHopHref(slug: string, next: string): string {
  const suffix = next.startsWith("/") ? next : `/${next}`;
  return (
    `/auth/hop?slug=${encodeURIComponent(slug)}` +
    `&next=${encodeURIComponent(suffix)}`
  );
}

const FALLBACK_PLATFORM_HOST = "forgmatic.com";

/**
 * Build an absolute URL into the SaaS marketing shell. Used by tenant /
 * platform-admin pages that need to cross-link to a route only mounted
 * on the apex (``/sign-up-shop``, ``/sign-up-team``, ``/pricing-plans``,
 * the sign-in redirect AdminLayout fires on an expired session).
 *
 * Same rule as ``buildTenantUrl``: the apex, scheme and port come from
 * the current deployment, so a session expiring on a dev server bounces
 * the user to the local sign-in rather than to production's.
 *
 * ``VITE_PLATFORM_HOST`` still wins when set — an explicit deployment
 * decision outranks derivation.
 */
export function buildPlatformUrl(path: string): string {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const env = (import.meta.env?.VITE_PLATFORM_HOST as string | undefined)?.trim();
  if (env && env.length > 0) return `https://${env}${suffix}`;
  const current = apexOriginOfKind("platform");
  if (current) {
    const port = current.port ? `:${current.port}` : "";
    return `${current.protocol}//${current.apex}${port}${suffix}`;
  }
  return `https://${FALLBACK_PLATFORM_HOST}${suffix}`;
}

/**
 * Build an absolute URL into the platform-admin shell (``admin.<apex>``).
 *
 * The staff console is a HOST, not a route: signing in on a tenant
 * subdomain leaves a platform admin with no in-app way to reach it — they
 * had to type the hostname from memory. The account menu links here.
 *
 * Same deployment rule as the two builders above: scheme, apex and port
 * come from ``window.location``, so a dev session hops to
 * ``admin.forgmatic.localhost:5174`` rather than to production. The apex
 * fallback applies off-deployment (SSR, Vercel preview, unknown domain).
 */
export function buildPlatformAdminUrl(path: string): string {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  // PLATFORM apex, explicitly: the staff console never lives in the
  // tenant zone, so standing on a tenant host must not build
  // ``admin.{tenantApex}`` — a hostname a stranger could have claimed.
  const current = apexOriginOfKind("platform");
  if (current) {
    const port = current.port ? `:${current.port}` : "";
    return `${current.protocol}//admin.${current.apex}${port}${suffix}`;
  }
  return `https://admin.${FALLBACK_PLATFORM_HOST}${suffix}`;
}

/**
 * Build an absolute URL into the Ingot doc web (``ingot.<apex>``).
 *
 * The design-system docs are a second rollup input of this very app
 * (``apps/web/ingot.html``, KAN-581), routed by a host-based rewrite in
 * ``vercel.json`` — so the host is derivable and must not be written
 * out as a literal. A hardwired ``ingot.forgmatic.com`` would also mean
 * that renaming the platform apex needs a code change.
 *
 * Same deployment rule as ``buildPlatformAdminUrl``: scheme, apex and
 * port come from the current deployment. The PLATFORM apex explicitly —
 * the docs live under ``.com`` on purpose, because on the tenant apex
 * ``apexFromHostname`` would read the subdomain as a tenant host and
 * fall through to ``DEFAULT_TENANT_SLUG`` (the KAN-11/KAN-39 trap).
 */
export function buildIngotDocsUrl(path = "/"): string {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const current = apexOriginOfKind("platform");
  if (current) {
    const port = current.port ? `:${current.port}` : "";
    return `${current.protocol}//ingot.${current.apex}${port}${suffix}`;
  }
  return `https://ingot.${FALLBACK_PLATFORM_HOST}${suffix}`;
}
